import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useApplicationActionButtons } from 'components/Applications/hooks/useApplicationActionButtons';
import Collapse from 'components/Collapse/Collapse';
import JobseekerAvatar from 'components/JobseekerAvatar/JobseekerAvatar';
import Tags from 'components/Jobseekers/components/Tags';
import SkillMatch from 'components/SkillMatch/SkillMatch';
import StageSelector from 'components/StageSelector/StageSelector';
import * as applicationConfig from 'components/StageSelector/clientApplicationConfig';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';

import useSidebarOpen from 'hooks/useSidebarOpen';

import handleClickOnActiveElement from 'utils/handleClickOnActiveElement';

import { Application } from 'types/applications';

import {
  Dictionaries,
  JobseekerAttachmentTypes,
  SidebarContentTypes
} from 'constants/enums';
import {
  FALLBACK_VALUE,
  JOBSEEKER_CARD_MINI_AVATAR_SIZE_PX
} from 'constants/uiSettings';

import styles from './ApplicationCardMini.module.scss';

interface ApplicationCardMiniProps {
  application: Application;
}

const ApplicationCardMini: FC<ApplicationCardMiniProps> = ({
  application
}: ApplicationCardMiniProps) => {
  const { t } = useTranslation();
  const clientApplicationStages = useSelector(
    selectDictionary(Dictionaries.ApplicationStageClient)
  );
  const clientApplicationTags = useSelector(
    selectDictionary(Dictionaries.ApplicationStageClientTag)
  );
  const openSidebar = useSidebarOpen();

  const jobseekerPhotoUrl = application?.attachments?.find(
    (attachment) => attachment.type === JobseekerAttachmentTypes.JobseekerPhoto
  )?.url;

  const { actionButtons, activeComponent } = useApplicationActionButtons({
    application
  });

  return (
    <div className={styles.wrap}>
      <div className={styles.content}>
        <div
          className={styles.title}
          onClick={openSidebar(
            SidebarContentTypes.ApplicationView,
            application.id
          )}
        >
          {application.jobName || FALLBACK_VALUE}
        </div>

        <div className={styles.status} onClick={handleClickOnActiveElement}>
          <StageSelector
            value={application.clientStage}
            id={application.id}
            options={clientApplicationStages}
            hasPermission
            config={applicationConfig}
            dictionary={Dictionaries.ApplicationClientStageFlow}
          />

          {application.tags && (
            <Tags
              dictionary={clientApplicationTags}
              values={application.tags}
              className={styles.tags}
            />
          )}
        </div>

        <div className={styles.jobseeker}>
          <JobseekerAvatar
            photoUrlEndpoint={jobseekerPhotoUrl}
            size={JOBSEEKER_CARD_MINI_AVATAR_SIZE_PX}
          />

          <div className={styles.jobseekerDetails}>
            {application.jobseekerFullName || FALLBACK_VALUE}

            <div onClick={handleClickOnActiveElement}>
              <SkillMatch matchData={application.matchData} />
            </div>
          </div>
        </div>

        {application.aiSummary ? (
          <div
            className={styles.aiSummary}
            onClick={handleClickOnActiveElement}
          >
            <Collapse>{application.aiSummary}</Collapse>
          </div>
        ) : (
          <div className={styles.aiSummaryFallback}>
            {t('card.jobseeker.aiSummary.placeholder')}
          </div>
        )}
      </div>

      <div className={styles.controls}>{actionButtons}</div>
      {activeComponent}
    </div>
  );
};

export default ApplicationCardMini;
